import { useEffect } from "react";
import "@/App.css";
import "@/styles/dojo-themes.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import HomePage from "@/pages/HomePage";
import MatchPage from "@/pages/MatchPage";
import StatsPage from "@/pages/StatsPage";
import MultiplaGolosaPage from "@/pages/MultiplaGolosaPage";
import MieiPronosticiPage from "@/pages/MieiPronosticiPage";
import SamuraiPage from "@/pages/SamuraiPage";
import SamuraiMatchPage from "@/pages/SamuraiMatchPage";
import SamuraiRoninPage from "@/pages/SamuraiRoninPage";
import DaymioTrendsPage from "@/pages/DaymioTrendsPage";
import SplashScreen from "@/components/SplashScreen";
import AdminPage from "@/pages/AdminPage";
import ExcelViewerPage from "@/pages/ExcelViewerPage";
import BrainCrackerPage from "@/pages/BrainCrackerPage";
import AccessGate from "@/components/AccessGate";
import AuthPage from "@/pages/AuthPage";
import PricingPage from "@/pages/PricingPage";
import AccountPage from "@/pages/AccountPage";
import BillingSuccessPage from "@/pages/BillingSuccessPage";
import { ForgotPasswordPage, ResetPasswordPage } from "@/pages/PasswordResetPages";
import { AuthProvider } from "@/contexts/AuthContext";
import DojoAmbience from "@/components/DojoAmbience";
import DojoSignature from "@/components/DojoSignature";
import CursorTrail from "@/components/CursorTrail";
import { applyDojoTheme, getStoredThemeId, getStoredMode } from "@/lib/dojoThemes";
import { getCursorPref, subscribeCursorPref } from "@/lib/cursorPref";

function App() {
  useEffect(() => {
    document.documentElement.classList.add("dark");
    document.title = "Bet Cracker";
    // Apply the user's last-selected Dojo theme on every page load.
    applyDojoTheme(getStoredThemeId(), getStoredMode());

    // Katana cursor — inject the cursor CSS at runtime (webpack's CSS
    // loader otherwise tries to bundle the PNG paths as modules). Toggle
    // a body class on mousedown/mouseup to swap to the "drawn blade"
    // sprite. Skipped on touch devices. The whole thing is gated by the
    // user-controlled pref in localStorage (lib/cursorPref.js) — when set
    // to "normal" the styles are removed and listeners detached so the
    // OS cursor takes over.
    const isTouch = window.matchMedia?.("(pointer: coarse)")?.matches;
    let onDown, onUp, styleEl, unsub;
    if (!isTouch) {
      const base = (process.env.PUBLIC_URL || "") + "/cursors";
      const installCursor = () => {
        if (styleEl) return;
        styleEl = document.createElement("style");
        styleEl.setAttribute("data-bc-cursor", "1");
        // 44px is the new default size (smaller than the original 60/64
        // for less visual weight); 32px stays as fallback for browsers
        // with a tighter cursor budget.
        styleEl.textContent = `
          html, body {
            cursor: url('${base}/katana-44.png') 40 4,
                    url('${base}/katana-32.png') 28 3, auto;
          }
          body.bc-cursor-click,
          body.bc-cursor-click * {
            cursor: url('${base}/katana-click-44.png') 41 4,
                    url('${base}/katana-click-32.png') 29 3, auto !important;
          }
          input, textarea, [contenteditable="true"],
          body.bc-cursor-click input,
          body.bc-cursor-click textarea,
          body.bc-cursor-click [contenteditable="true"] {
            cursor: text !important;
          }
        `;
        document.head.appendChild(styleEl);
        onDown = () => document.body.classList.add("bc-cursor-click");
        onUp   = () => document.body.classList.remove("bc-cursor-click");
        window.addEventListener("mousedown", onDown);
        window.addEventListener("mouseup", onUp);
        window.addEventListener("blur", onUp);
      };
      const uninstallCursor = () => {
        if (onDown) { window.removeEventListener("mousedown", onDown); onDown = null; }
        if (onUp) {
          window.removeEventListener("mouseup", onUp);
          window.removeEventListener("blur", onUp);
          onUp = null;
        }
        document.body.classList.remove("bc-cursor-click");
        if (styleEl && styleEl.parentNode) {
          styleEl.parentNode.removeChild(styleEl);
          styleEl = null;
        }
      };
      // Initial install based on stored pref
      if (getCursorPref() === "katana") installCursor();
      // React to user toggling the cursor at runtime
      unsub = subscribeCursorPref((next) => {
        if (next === "katana") installCursor();
        else uninstallCursor();
      });
    }
    // Notify the Emergent preview wrapper (outer iframe host) in case it
    // listens for a title update — no-op in production where we're not
    // framed. Safe even if window.parent === window.
    try {
      if (window.parent && window.parent !== window) {
        window.parent.postMessage({ type: "title", value: "Bet Cracker" }, "*");
      }
    } catch { /* cross-origin access blocked, ignore */ }

    return () => {
      if (onDown) window.removeEventListener("mousedown", onDown);
      if (onUp) {
        window.removeEventListener("mouseup", onUp);
        window.removeEventListener("blur", onUp);
      }
      if (styleEl && styleEl.parentNode) styleEl.parentNode.removeChild(styleEl);
      if (unsub) unsub();
    };
  }, []);

  return (
    <div className="App">
      <DojoSignature />
      <DojoAmbience />
      <CursorTrail />
      <SplashScreen />
      <BrowserRouter>
        <AuthProvider>
        <Routes>
          {/* Admin panel — has its own auth, outside the access gate */}
          <Route path="/admin" element={<AdminPage />} />

          {/* Excel-style viewer — own access code, outside the main gate */}
          <Route path="/ExcelViewer" element={<ExcelViewerPage />} />
          <Route path="/excelviewer" element={<ExcelViewerPage />} />

          {/* Email/password auth (freemium) — outside the gate */}
          <Route path="/auth" element={<AuthPage />} />

          {/* Password reset flow — outside the gate */}
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Public pricing page — must be reachable without auth so the
              CTA-overlay click from PremiumGate works for any visitor. */}
          <Route path="/pricing" element={<PricingPage />} />

          {/* Account/billing — requires JWT auth, redirects to /auth otherwise */}
          <Route path="/account" element={<AccountPage />} />

          {/* Stripe Checkout success/cancel landing pages */}
          <Route path="/billing/success" element={<BillingSuccessPage />} />

          {/* All other routes protected by AccessGate */}
          <Route path="/*" element={
            <AccessGate>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/match/:fixtureId" element={<MatchPage />} />
                <Route path="/stats" element={<StatsPage />} />
                <Route path="/multipla" element={<MultiplaGolosaPage />} />
                <Route path="/miei-pronostici" element={<MieiPronosticiPage />} />
                <Route path="/samurai" element={<SamuraiPage />} />
                <Route path="/samurai/ronin" element={<SamuraiRoninPage />} />
                <Route path="/samurai/:fixtureId" element={<SamuraiMatchPage />} />
                <Route path="/daymio-trends" element={<DaymioTrendsPage />} />
                <Route path="/brain-cracker" element={<BrainCrackerPage />} />
                {/* Legacy redirect — old Shogun-only Learning Lab */}
                <Route path="/learning-lab" element={<BrainCrackerPage />} />
              </Routes>
            </AccessGate>
          } />
        </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
